import type { EditorView } from "@codemirror/view";
import { designVariables } from "@studio3t/design-variables";
import {
  ButtonSmallSecondary,
  ButtonSmallSubtleLeadingIcon,
  Icon,
  IconSVG,
} from "@studio3t/generic-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { useTheme } from "styled-components";
import { useAiChatContext } from "@/components/ai/chat";
import { EDITOR_FONT } from "@/components/JsonViewer";
import { OverlayScroll } from "@/components/OverlayScroll";
import {
  type EditorErrorRow,
  errorCountLabel,
  errorRowLabel,
  fixWithAiMessage,
} from "./editorErrors";
import { useLabeledButton } from "./PipelineNavBar";

// Figma ErrorList (15067-167750): the status bar's "N errors" button
// (Bridge_PipelineCode 14785-265837: Button/S subtle, error icon, red
// text) opens a card listing the lint errors — "Errors" title, one mono row per error
// with a chevron, a "Fix with AI" button. A row moves the cursor to the
// error; the button hands the list to the AI Helper. The card floats
// above the status bar (portaled, fixed) so the editor's overflow never
// clips it; Escape, a click outside or a row click closes it.

const CARD_GAP = 4;
// The design's fixed card: 320 wide, a two-row list region (60px) that
// scrolls when there are more errors, so the card never changes size.
const CARD_WIDTH = 320;
const ROW_HEIGHT = 30;
const VISIBLE_ROWS = 2;

/** A row's jump: cursor on the error, scrolled into view, editor focused. */
export function jumpToOffset(view: EditorView | undefined, from: number) {
  if (!view) return;
  view.dispatch({ selection: { anchor: from }, scrollIntoView: true });
  view.focus();
}

interface Anchor {
  bottom: number;
  right: number;
}

function anchorFor(el: HTMLElement): Anchor {
  const rect = el.getBoundingClientRect();
  return {
    bottom: window.innerHeight - rect.top + CARD_GAP,
    right: window.innerWidth - rect.right,
  };
}

// Escape and outside clicks close the card; the trigger's own click is
// left to its toggle.
function useDismiss(
  open: boolean,
  close: () => void,
  refs: React.RefObject<HTMLElement>[],
) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onPointer = (e: MouseEvent) => {
      const inside = refs.some((r) => r.current?.contains(e.target as Node));
      if (!inside) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open, close, refs]);
}

// "Fix with AI": opens the helper if collapsed and sends the list as the
// user's message (the sidebar submits it with the view's context). Shared
// with the toolbar's "Fix the code errors first" dialog, so both buttons
// hand the helper the same message.
export function useFixWithAi(
  documentName: string,
  rows: readonly EditorErrorRow[],
  /** The document's text, sent in the message when its structured context
   *  cannot stand in for it (the pipeline code). */
  code?: string,
) {
  const { queueMessage, setShowAiSidebar } = useAiChatContext();
  return useCallback(() => {
    queueMessage(fixWithAiMessage(documentName, rows, code));
    setShowAiSidebar(true);
  }, [queueMessage, setShowAiSidebar, documentName, rows, code]);
}

function ErrorsCard({
  anchor,
  rows,
  documentName,
  code,
  onJump,
  onClose,
  cardRef,
  testIdBase,
}: {
  anchor: Anchor;
  rows: readonly EditorErrorRow[];
  documentName: string;
  code?: string;
  onJump: (from: number) => void;
  onClose: () => void;
  cardRef: React.RefObject<HTMLDivElement>;
  testIdBase: string;
}) {
  const theme = useTheme();
  const fixWithAi = useFixWithAi(documentName, rows, code);
  return createPortal(
    <Card
      ref={cardRef}
      role="dialog"
      aria-label="Errors"
      style={{ bottom: anchor.bottom, right: anchor.right }}
      data-testid={`${testIdBase}-errors`}
    >
      <CardTitle>Errors</CardTitle>
      <RowList>
        {rows.map((row) => (
          <ErrorRow
            key={`${row.from}:${row.message}`}
            type="button"
            title={errorRowLabel(row)}
            onClick={() => {
              onClose();
              onJump(row.from);
            }}
          >
            <RowText>{errorRowLabel(row)}</RowText>
            <RowChevron aria-hidden="true">
              <IconSVG
                icon={Icon.Chevron}
                size={designVariables.ui.list.item.chevron}
                colors={{ default: theme.semanticTextPlaceholder }}
              />
            </RowChevron>
          </ErrorRow>
        ))}
      </RowList>
      <CardFooter>
        <ButtonSmallSecondary
          text="Fix with AI"
          onClick={() => {
            onClose();
            fixWithAi();
          }}
        />
      </CardFooter>
    </Card>,
    document.body,
  );
}

/** The status bar's "N errors" button as the card's trigger: a click
 *  toggles the list; the hover title stays as the one-line summary. */
export function ErrorStatusButton({
  rows,
  title,
  documentName,
  code,
  onJump,
  testIdBase,
}: {
  rows: readonly EditorErrorRow[];
  title: string;
  documentName: string;
  /** The document's text for "Fix with AI", when the helper's context
   *  item cannot stand in for it (the pipeline code). */
  code?: string;
  onJump: (from: number) => void;
  /** data-testid prefix: `${base}-validity` (the trigger), `${base}-errors`. */
  testIdBase: string;
}) {
  const theme = useTheme();
  // The lib button drops title/aria-label props — set through the ref.
  const triggerRef = useLabeledButton(title);
  const cardRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const close = useCallback(() => setAnchor(null), []);
  const refs = useRef<React.RefObject<HTMLElement>[]>([
    triggerRef,
    cardRef,
  ]).current;
  useDismiss(anchor !== null, close, refs);

  const toggle = () => {
    if (anchor) {
      close();
      return;
    }
    if (triggerRef.current) setAnchor(anchorFor(triggerRef.current));
  };

  const red = theme.statusError;
  return (
    <>
      <Trigger aria-expanded={anchor !== null}>
        <ButtonSmallSubtleLeadingIcon
          ref={triggerRef}
          icon={Icon.Error}
          colors={{ default: red, hover: red, active: red, focus: red }}
          text={errorCountLabel(rows.length)}
          active={anchor !== null}
          onClick={toggle}
          testId={`${testIdBase}-validity`}
        />
      </Trigger>
      {anchor && (
        <ErrorsCard
          anchor={anchor}
          rows={rows}
          documentName={documentName}
          code={code}
          onJump={onJump}
          onClose={close}
          cardRef={cardRef}
          testIdBase={testIdBase}
        />
      )}
    </>
  );
}

// The lib's subtle button with its text painted in the error colour (the
// icon takes its colours as a prop; the text has no such prop).
const Trigger = styled.span`
  display: inline-flex;

  button {
    color: ${(p) => p.theme.statusError};
  }
`;

// The design's card: app background, 6px corner, elevation/outset4.
const Card = styled.div`
  position: fixed;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH}px;
  max-width: calc(100vw - 16px);
  overflow: hidden;
  background: ${(p) => p.theme.semanticAppBackground};
  border-radius: 6px;
  box-shadow:
    0 7px 33px rgba(17, 17, 17, 0.12),
    0 2px 6px rgba(17, 17, 17, 0.2);
`;

const CardTitle = styled.div`
  padding: ${designVariables.ui.semantic.spacings}px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextHeading};
`;

// The list region keeps its two-row height whatever the count; the app's
// OverlayScrollbars own the overflow beyond it.
const RowList = styled(OverlayScroll)`
  height: ${ROW_HEIGHT * VISIBLE_ROWS}px;
  flex-shrink: 0;

  [data-overlayscrollbars-contents] {
    display: flex;
    flex-direction: column;
  }
`;

// List/item: 30px row, 10px side padding, 6px gap, mono text.
const ErrorRow = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: ${ROW_HEIGHT}px;
  flex-shrink: 0;
  padding: 0 ${designVariables.ui.semantic.spacings}px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  min-width: 0;

  &:hover,
  &:focus-visible {
    background: ${designVariables.primitives.neutrals.alpha.b2};
    outline: none;
  }
`;

const RowText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${EDITOR_FONT};
  font-size: 12px;
  line-height: 1;
  color: ${(p) => p.theme.semanticTextBody};
`;

const RowChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const CardFooter = styled.div`
  display: flex;
  padding: ${designVariables.ui.semantic.spacings}px;
`;
